import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
// import { getLocale } from "react-i18nify";

import { TextField, Button, Typography, Paper, Box, Grid } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { newPassWord, recoverPassword, getUserInfo } from "src/api/auth";

import device from "src/styles/device";
import { Primary, White } from "src/styles/color";
import { useAuth } from "src/hooks/useAuth";
import { LoadingContext } from "src/context/LoadingContext";

// Validation schema
const schemaLogin = yup.object().shape({
  email: yup.string().email("รูปแบบอีเมลไม่ถูกต้อง").required("กรุณากรอกอีเมล"),
  password: yup
    .string()
    .required("กรุณากรอกรหัสผ่าน")
    .min(8, "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร"),
});

const schemaForgot = yup.object().shape({
  email: yup.string().email("รูปแบบอีเมลไม่ถูกต้อง").required("กรุณากรอกอีเมล"),
});

const schemaChange = yup.object().shape({
  password: yup
    .string()
    .required("กรุณากรอกรหัสผ่าน")
    .min(8, "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร"),
  con_password: yup
    .string()
    .oneOf([yup.ref("password"), null], "รหัสผ่านไม่ตรงกัน")
    .required("กรุณากรอกรหัสผ่าน"),
});

const SellerImage = styled.img`
  width: 90%;
  object-fit: contain;
  object-position: center;

  @media ${device.mobileL} {
    display: none;
  }
`;

const NavigationBar = styled.div`
  width: 100%;
  height: 60px;
  background-color: black;
  position: fixed;
  top: 0;
  left: 0;
`;

const WrapNavbar = styled.div`
  width: 100%;
  max-width: 1440px;
  height: 100%;
  margin: auto;
  display: flex;
  padding: 0 100px;

  @media ${device.tabletL} {
    padding: 0 40px;
  }
  @media ${device.mobileL} {
    padding: 0 24px;
  }
`;

const Logo = styled.img`
  width: 180px;

  @media ${device.tabletL} {
    width: 150px;
  }
`;

// const WrapLogo = styled.div`
//   width: 25px;
//   height: 25px;
//   margin: 0 10px;
//   position: relative;
//   cursor: pointer;
//   transition: 0.3s;

//   :hover {
//     transform: scale(1.1);
//   }

//   @media ${device.tablet} {
//     margin: 0 5px;
//   }
// `;

const SellerNavbar = styled.h2`
  color: ${White};
  font-weight: 400;
  margin: 0 0 0 10px;

  ::before {
    content: "|";
    width: 1px;
    height: 100%;
    margin-right: 5px;

    @media ${device.mobileL} {
      display: none;
    }
  }

  @media ${device.tabletL} {
    font-size: 1.4rem;
  }
  @media ${device.mobileL} {
    margin: 0;
    display: none;
  }
`;

// const LogoSocial = styled.img`
//   width: 100%;
//   height: 100%;
// `;

const TypographyCustom = styled(Typography)`
  font-size: 1rem;
  cursor: pointer;
  transition: 0.3s;
  text-decoration: underline;

  :hover {
    text-decoration: underline;
  }

  /* ::after {
    content: "|";
    width: 1px;
    height: 100%;
    margin-left: 10px;
  } */
`;

const WrapSeller = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 20px;

  @media ${device.mobileL} {
    margin-bottom: 0;
  }
`;

const Login = () => {
  const navigate = useNavigate();

  const [mode, setMode] = useState("login");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      mode === "login"
        ? schemaLogin
        : mode === "forgot"
          ? schemaForgot
          : schemaChange,
    ),
  });
  const { setLoading } = useContext(LoadingContext);
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [token, setToken] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token")?.replace(/}/g, "");
    if (token) {
      setToken(token);
      setMode("reset");
    } else {
      setMode("login");
    }
  }, []);

  const onSubmit = async (data) => {
    const pathname = window.location.pathname;

    setLoading(true);
    let info = {
      ...data,
      login_type: 3,
    };

    setIsDisabled(true);
    if (mode === "login") {
      login(info)
        .then(async () => {
          enqueueSnackbar("เข้าสู่ระบบสำเร็จ", {
            variant: "success",
            autoHideDuration: 3000,
            onClose: (event, reason, myKey) => {
              if (reason === "timeout") {
                setIsDisabled(false);
              }
            },
          });
          const userInfo = await getUserInfo();
          if (userInfo?.role_id === 3) {
            navigate("/profile");
          } else if (pathname !== "/") {
            navigate(pathname);
          } else {
            navigate("/dashboard-sales-performance");
          }
        })
        .catch((error) => {
          enqueueSnackbar(
            error.message.th.title + " " + error.message.th.desc,
            {
              variant: "error",
              autoHideDuration: 3000,
              onClose: (event, reason, myKey) => {
                if (reason === "timeout") {
                  setIsDisabled(false);
                }
              },
            },
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (mode === "forgot") {
      recoverPassword({ email: data.email })
        .then((result) => {
          enqueueSnackbar(
            result.message.th.title + " " + result.message.th.desc,
            {
              variant: "success",
              autoHideDuration: 4500,
              onClose: (event, reason, myKey) => {
                if (reason === "timeout") {
                  window.location.replace("https://manage.xsurface.com");
                  setIsDisabled(false);
                }
              },
            },
          );
        })
        .catch((error) => {
          enqueueSnackbar(
            error.response.data.message.th.title +
              " " +
              error.response.data.message.th.desc,
            {
              variant: "error",
              autoHideDuration: 3000,
              onClose: (event, reason, myKey) => {
                if (reason === "timeout") {
                  setIsDisabled(false);
                }
              },
            },
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (mode === "reset") {
      newPassWord({ token, password: data.password })
        .then((result) => {
          enqueueSnackbar(
            result.message.th.title + " " + result.message.th.desc,
            {
              variant: "success",
              autoHideDuration: 4500,
              onClose: (event, reason, myKey) => {
                if (reason === "timeout") {
                  window.location.replace("https://manage.xsurface.com");
                  setIsDisabled(false);
                }
              },
            },
          );
        })
        .catch((error) => {
          enqueueSnackbar(
            error.response.data.message.th.title +
              " " +
              error.response.data.message.th.desc,
            {
              variant: "error",
              autoHideDuration: 3000,
              onClose: (event, reason, myKey) => {
                if (reason === "timeout") {
                  setIsDisabled(false);
                }
              },
            },
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <>
      <NavigationBar>
        <WrapNavbar>
          <Grid container xs={12} justifyContent="space-between">
            <Grid item xs={6} sm={8} display="flex" alignItems="center">
              <Logo src="https:///xsurface-images.s3.ap-southeast-1.amazonaws.com/icon/_icon%20set_Logo%20xsurface_w_w.svg" />

              <SellerNavbar>Seller Center</SellerNavbar>
            </Grid>

            <Grid
              item
              xs={6}
              sm={4}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <TypographyCustom
                color={White}
                variant="h7"
                fontWeight={400}
                sx={{
                  mr: { xs: 0.5, sm: 1 },
                  fontSize: { xs: "0.8rem", md: "1rem" },
                }}
                onClick={() => window.location.replace("https://xsurface.com")}
              >
                กลับสู่หน้าเลือกสินค้า
              </TypographyCustom>

              {/* <WrapLogo
                onClick={() =>
                  window.open("https://www.facebook.com/XSURFACEofficial")
                }
              >
                <LogoSocial src="https://xsurface-images.s3.ap-southeast-1.amazonaws.com/icon/_icon%20set_Facebook_ori.svg" />
              </WrapLogo> */}

              {/* <WrapLogo
                onClick={() =>
                  window.open(
                    "https://line.me/R/ti/p/@875eatlu?oat_content=url&ts=04291658",
                  )
                }
              >
                <LogoSocial src="https://xsurface-images.s3.ap-southeast-1.amazonaws.com/icon/Line-Icon-Circle.png" />
              </WrapLogo> */}
            </Grid>
          </Grid>
        </WrapNavbar>
      </NavigationBar>

      <Box
        sx={{
          backgroundColor: "#252525",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
          flexDirection: "column",
        }}
      >
        <Grid
          container
          xs={12}
          sx={{ maxWidth: "1440px !important", px: { xs: 0, sm: 5, lg: 10 } }}
        >
          <Grid
            item
            xs={10}
            md={5.5}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            sx={{ mt: { xs: 6, sm: 12, lg: 0 }, mx: { xs: "auto" } }}
          >
            <WrapSeller>
              <Typography
                variant="h3"
                color={White}
                fontWeight={500}
                textAlign="center"
                sx={{ fontSize: { xs: "2rem" } }}
              >
                Seller Center
              </Typography>

              <Typography
                variant="h6"
                fontWeight={500}
                color={White}
                sx={{
                  textAlign: "center",
                  marginTop: { xs: "10px", sm: "0px" },
                }}
              >
                จัดการร้านค้าของคุณอย่างมืออาชีพ
                เริ่มต้นธุรกิจวัสดุตกแต่งได้ที่นี่
              </Typography>
            </WrapSeller>

            <SellerImage src="https://image.xsurface.com/BecomeSeller/messageImage_1745827777662%20logo-01-01-Photoroom.png" />
          </Grid>

          <Grid
            item
            xs={12}
            md={6.5}
            sx={{
              pl: { xs: 0, md: 6, lg: 15 },
              justifyItems: { xs: "center", md: "flex-end" },
              my: { xs: 0, sm: 8, md: 0 },
              height: { xs: "fit-content", sm: "unset" },
            }}
            alignContent="center"
          >
            {mode === "login" ? (
              <Paper
                elevation={3}
                fullWidth
                sx={{
                  width: { xs: "320px", sm: "360px", lg: "400px" },
                  display: "flex",
                  height: "400px",
                  borderRadius: "16px",
                }}
                style={{ margin: "auto" }}
              >
                <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
                  <Typography variant="h5" gutterBottom textAlign="center">
                    เข้าสู่ระบบร้านค้า
                  </Typography>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                      fullWidth
                      label="อีเมล"
                      margin="normal"
                      {...register("email")}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                    <TextField
                      fullWidth
                      type="password"
                      label="รหัสผ่าน"
                      margin="normal"
                      {...register("password")}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                    <Grid container xs={12} textAlign="end">
                      <Typography
                        width="100%"
                        textAlign="end"
                        variant="h12"
                        sx={{
                          textDecoration: "underline",
                          color: Primary,
                          cursor: "pointer",
                        }}
                        onClick={() => setMode("forgot")}
                      >
                        ลืมรหัสผ่าน
                      </Typography>
                    </Grid>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 2,
                        backgroundColor: Primary,

                        "&:hover": {
                          backgroundColor: Primary,
                        },
                      }}
                      disabled={isDisabled}
                    >
                      ลงชื่อเข้าใช้
                    </Button>
                  </form>
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    ยังไม่ได้เป็นผู้ขาย?{" "}
                    <Typography
                      variant="caption"
                      sx={{
                        textDecoration: "underline",
                        color: Primary,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open("https://xsurface.com/become-a-seller")
                      }
                    >
                      สมัครเป็นผู้ขาย
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            ) : mode === "forgot" ? (
              <Paper
                elevation={3}
                fullWidth
                sx={{
                  width: { xs: "320px", sm: "360px", lg: "400px" },
                  display: "flex",
                  height: "400px",
                  borderRadius: "16px",
                }}
                style={{ margin: "auto" }}
              >
                <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }} position="relative">
                  <Typography
                    variant="h5"
                    gutterBottom
                    justifyContent="center"
                    display="flex"
                    alignItems="center"
                  >
                    <ArrowBackIcon
                      sx={{
                        position: "absolute",
                        left: "24px",
                        cursor: "pointer",
                      }}
                      onClick={() => setMode("login")}
                    />
                    รีเซ็ตรหัสผ่าน
                  </Typography>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                      fullWidth
                      label="อีเมล"
                      margin="normal"
                      {...register("email")}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 2,
                        backgroundColor: Primary,

                        "&:hover": {
                          backgroundColor: Primary,
                        },
                      }}
                      disabled={isDisabled}
                    >
                      ส่งคำขอรีเซ็ตรหัสผ่าน
                    </Button>
                  </form>
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    ยังไม่ได้เป็นผู้ขาย?{" "}
                    <Typography
                      variant="caption"
                      sx={{
                        textDecoration: "underline",
                        color: Primary,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open("https://xsurface.com/become-a-seller")
                      }
                    >
                      สมัครเป็นผู้ขาย
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            ) : (
              <Paper
                elevation={3}
                fullWidth
                sx={{
                  width: { xs: "320px", sm: "360px", lg: "400px" },
                  display: "flex",
                  height: "400px",
                  borderRadius: "16px",
                }}
                style={{ margin: "auto" }}
              >
                <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
                  <Typography variant="h5" gutterBottom textAlign="center">
                    เปลี่ยนรหัสผ่าน
                  </Typography>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                      fullWidth
                      type="password"
                      label="รหัสผ่าน"
                      margin="normal"
                      {...register("password")}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                    <TextField
                      fullWidth
                      type="password"
                      label="ยืนยันรหัสผ่าน"
                      margin="normal"
                      {...register("con_password")}
                      error={!!errors.con_password}
                      helperText={errors.con_password?.message}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 2,
                        backgroundColor: Primary,

                        "&:hover": {
                          backgroundColor: Primary,
                        },
                      }}
                      disabled={isDisabled}
                    >
                      ส่งคำขอเปลี่ยนรหัสผ่าน
                    </Button>
                  </form>
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    ยังไม่ได้เป็นผู้ขาย?{" "}
                    <Typography
                      variant="caption"
                      sx={{
                        textDecoration: "underline",
                        color: Primary,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open("https://xsurface.com/become-a-seller")
                      }
                    >
                      สมัครเป็นผู้ขาย
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Login;
